import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useHistory } from "react-router-dom";
import { Responsive, WidthProvider } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { useRecoilValue } from "recoil";
import { wsReadyState, userInfoState } from "../global-state";
import UpsertWidgetDialog from "./upsert-widget-dialog";
import UpsertDeviceDialog from "./upsert-device-dialog";
import { useWebSocket } from "../ws/websocket";
import { useEventEmitter } from "../ws/event-context";
import {
  deleteCard,
  updateCanvasCards,
  updateCardLayout,
  updateCardContent,
  createWidgetCard,
  moveCanvas,
  batchDeleteCard,
  batchMoveCanvas,
  getCanvasContent,
} from "../service/ui-service";
import {
  createWidgets,
  triggerWidgetEvent,
  updateWidgetData,
} from "../service/widget-service";
import { useToast } from "../common/toast";
import { useBackdrop } from "../common/backdrop";
import { useMediaQuery } from "@mui/material";
import {
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  IconButton,
  Typography,
  Checkbox,
} from "@mhome/ui";
import { Menu, MenuItem, MenuItemIcon, MenuItemText } from "@mhome/ui";
import DashboardCard from "./dashboard-card";
import EditCardDialog from "./edit-card-dialog";
import DeleteCardDialog, { BatchDeleteDialog } from "./delete-card-dialog";
import DeleteIcon from "@mui/icons-material/Delete";
import AutoModeIcon from "@mui/icons-material/AutoMode";
import CloseIcon from "@mui/icons-material/Close";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import AddOptionDialog from "./add-option-dialog";
import { listCanvas } from "../service/ui-service";
import { copyCard } from "../service/ui-service";
import { ContentCopy } from "@mui/icons-material";
import GridOnIcon from "@mui/icons-material/GridOn";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CardContentEditDialog from "./card-content-edit-dialog";
import { isEqual } from "lodash";
import CircularProgress from "@mui/material/CircularProgress";
import { alpha } from "@mui/material/styles";
import { getCardColor } from "../common/colors";
import CanvasSelectDialog from "./canvas-select-dialog";
import Markdown from "../common/markdown";
import { dashboardGuideContent } from "./dashboard-guide";
import TempStack from "./temp-stack";
import { calculateGridPosition, getGridCols } from "./grid-position-utils";
import { useComponentBackgroundColor } from "../common/adaptive-theme-provider";

const ResponsiveGridLayout = WidthProvider(Responsive);

const generateLayouts = (cardData, cols, layoutSize) => {
  let nextY = 0;
  let nextX = 0;

  return cardData.map((card) => {
    let { w, h, x, y } = card.layouts[layoutSize] || {};

    if (x === null || y === null || x === undefined || y === undefined) {
      // Auto-calculate x and y for missing ones
      if (nextX + w > cols) {
        nextX = 0;
        nextY += h; // Move to next row if space is not enough
      }
      x = nextX;
      y = nextY;
      nextX += w;
    } else {
      // If x and y are provided, move nextX and nextY accordingly
      nextX = Math.max(nextX, x + w);
      nextY = Math.max(nextY, y);
    }

    return { i: card.id, x, y, w, h };
  });
};

const DashboardCanvas = forwardRef(
  (
    {
      canvasId,
      drag = false,
      editCardLayoutMode = false,
      setEditCardLayoutMode = () => {},
      isChatSidebarVisible = false,
      onTempStackChange = () => {},
    },
    ref
  ) => {
    const { openBackdrop, closeBackdrop } = useBackdrop();
    const wsReady = useRecoilValue(wsReadyState);
    const { sendAndWaitWs } = useWebSocket();
    const { subscribe } = useEventEmitter();
    const [currentWidgets, setCurrentWidgets] = useState([]);
    const [currentCard, setCurrentCard] = useState(null);
    const [currentEditCard, setCurrentEditCard] = useState(null);
    const { successToast, errorToast } = useToast();
    const [layouts, setLayouts] = useState(null);

    const [contentLoading, setContentLoading] = useState(false);
    const widget2CardRef = useRef(new Map());
    const cardMapRef = useRef(new Map());
    const widgetMapRef = useRef(new Map());
    const [cardList, setCardList] = useState([]);
    const skipEvent = useRef(false);
    const [layoutChanged, setLayoutChanged] = useState(false);
    const [openOptionDialog, setOpenOptionDialog] = useState(false);
    const [openWidgetDialog, setOpenWidgetDialog] = useState(false);
    const [openDeviceDialog, setOpenDeviceDialog] = useState(false);
    const [openCardDialog, setOpenCardDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [widgetNames, setWidgetNames] = useState([]);
    const [isUpsertingWidget, setIsUpsertingWidget] = useState(false);
    const history = useHistory();
    const [canvasList, setCanvasList] = useState([]);
    const [isCanvasListLoaded, setIsCanvasListLoaded] = useState(false);
    const canvasListLoadingRef = useRef(false);
    const [editCard, setEditCard] = useState(null);
    const [isEditTitle, setIsEditTitle] = useState(null);
    const [tempLayout, setTempLayout] = useState(null);
    const [originalCardLayout, setOriginalCardLayout] = useState(null); // Store original card layout
    const [loadingSave, setLoadingSave] = useState(false);
    const [anchorElMap, setAnchorElMap] = useState({});
    // Test: Add a test anchor element for debugging
    const userInfo = useRecoilValue(userInfoState);
    const isInitialized = useRef(false);
    const loadedCanvasId = useRef("");
    const [selectedCards, setSelectedCards] = useState([]);
    const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
    const [isBatchDeleting, setIsBatchDeleting] = useState(false);
    const isMovingCardRef = useRef(false);

    // Track previous drag state to detect when drag changes from true to false
    const previousDragRef = useRef(false);

    const [isBatchDeleteDialogOpen, setIsBatchDeleteDialogOpen] =
      useState(false);

    // Add state for move dialog
    const [moveDialogOpen, setMoveDialogOpen] = useState(false);
    const [currentMoveCard, setCurrentMoveCard] = useState(null);

    // Add state for batch move dialog
    const [isBatchMoveDialogOpen, setIsBatchMoveDialogOpen] = useState(false);
    const [isBatchMoving, setIsBatchMoving] = useState(false);

    const scrollDirectionRef = useRef(0);
    const scrollAnimationRef = useRef(null);
    const containerRef = useRef(null);

    const stackedIds = useRef(new Set());

    const isMdOrLgScreen = useMediaQuery("@media (min-width:960px)");

    // Add state for dashboard guide dialog
    const [isGuideDialogOpen, setIsGuideDialogOpen] = useState(false);
    const [guideMarkdownContent, setGuideMarkdownContent] = useState("");

    // Add state for temporary stack
    const [stackedItems, setStackedItems] = useState([]);
    const [isDraggingOverStack, setIsDraggingOverStack] = useState(false);
    const droppingToStackRef = useRef(false);

    const setCardAnchorEl = (cardId, anchorEl) => {
      setAnchorElMap((prev) => ({ ...prev, [cardId]: anchorEl }));
    };

    const getCardAnchorEl = (cardId) => {
      return anchorElMap[cardId] || null;
    };

    const closeCardMenu = (cardId) => {
      setAnchorElMap((prev) => {
        const newMap = { ...prev };
        delete newMap[cardId];
        return newMap;
      });
    };

    // Notify parent of temp stack changes
    useEffect(() => {
      onTempStackChange(stackedItems.length > 0);
    }, [stackedItems.length, onTempStackChange]);

    // Add state for chip drag preview
    const [chipDragPreview, setChipDragPreview] = useState(null);

    // Load dashboard guide markdown content
    useEffect(() => {
      if (isGuideDialogOpen) {
        // Replace the generic text with current layout size info
        const layoutSize = getCurrentLayoutSize();
        const layoutName =
          layoutSize === "sm"
            ? "small screen"
            : layoutSize === "md"
            ? "medium screen"
            : "large screen";
        const content = dashboardGuideContent.replace(
          "You are currently editing your dashboard layout.",
          `You are currently editing the **${layoutName}** layout of your dashboard.`
        );
        setGuideMarkdownContent(content);
      }
    }, [isGuideDialogOpen]);

    useEffect(() => {
      const { unsubscribe } = subscribe((target, data) => {
        if (skipEvent.current) {
          return;
        }
        if (target === "/widget/reportUpdate") {
          const payload = JSON.parse(data.payload);
          const widgets = [];
          for (let change of payload.changes) {
            if (change.type === "UPSERT") {
              widgets.push(change.widget);
            }
          }
          if (widgets.length > 0) {
            handleWidgetUpdateEvent(widgets);
          }
        } else if (target === "/ui/canvas/reportUpdate") {
          const change = JSON.parse(data.payload);
          if (change.type === "UPDATE_CANVAS" && change.canvasId === canvasId) {
            handleCanvasUpdateEvent(change.revision);
          }
        } else if (target === "/ui/card/reportUpdate") {
          const payload = JSON.parse(data.payload);
          for (let change of payload.changes) {
            if (change.type === "UPSERT") {
              handleCardUpdateEvent(change.card);
            }
          }
        } else if (target === "/automation/runningStatus/reportUpdate") {
          const payload = JSON.parse(data.payload);
          if (payload) {
            handleAutomationRunningEvent(payload);
          }
        }
      });
      return () => {
        unsubscribe();
      };
    }, [canvasId]);

    useEffect(() => {
      const dragChangedFromTrueToFalse = previousDragRef.current && !drag;

      if (layoutChanged && dragChangedFromTrueToFalse) {
        if (!isMovingCardRef.current) {
          handleUpdateCardLayout();
        }
        setLayoutChanged(false);
      }
      if (!drag) {
        isMovingCardRef.current = false;
      }

      // Update previous drag state for next render
      previousDragRef.current = drag;
    }, [drag]);

    const handleCanvasUpdateEvent = (lr) => {
      loadCanvasData(true);
    };

    const handleWidgetUpdateEvent = (widgets) => {
      if (!isInitialized.current) {
        console.debug("Dashboard not initialized yet, ignoring widget update");
        return;
      }

      const widgetsArray = Array.isArray(widgets) ? widgets : [widgets];
      let hasUpdates = false;

      for (const widget of widgetsArray) {
        let wid = widget.id;

        if (!widget2CardRef.current.get(wid)) {
          // not related widget, ignore
          continue;
        }

        let nw = widget;
        if (wid.startsWith("P-") || wid.startsWith("Q-")) {
          const pw = widgetMapRef.current.get(wid);
          if (pw && pw.items) {
            const nItems = Object.fromEntries(
              Object.entries(pw.items).map(([key, item]) => {
                const updatedItem = widget.items[key];
                return [key, updatedItem || item];
              })
            );
            nw = { ...pw, items: nItems };
          }
        }
        widgetMapRef.current.set(wid, nw);

        for (const cardId of widget2CardRef.current.get(wid)) {
          const card = cardMapRef.current.get(cardId);
          if (card) {
            const newWidgets = card.widgets.map((w) =>
              w.id === wid ? { ...w, ...nw, prepared: false } : w
            );
            const newCard = { ...card, widgets: newWidgets };
            cardMapRef.current.set(newCard.id, newCard);
            hasUpdates = true;
          }
        }
      }

      if (hasUpdates) {
        setCardList(Array.from(cardMapRef.current.values()));
        setWidgetNames(
          Array.from(widgetMapRef.current.values()).map((w) => w.name)
        );
      }
    };

    const handleCardUpdateEvent = (card) => {
      if (!isInitialized.current) {
        console.debug("Dashboard not initialized yet, ignoring card update");
        return;
      }

      const existingCard = cardMapRef.current.get(card.cardId);
      if (!existingCard) {
        return;
      }

      const updatedCard = {
        ...existingCard,
        setting: card.setting || existingCard.setting,
        widgets: existingCard.widgets.map((widget) => {
          const updatedWidget = card.widgets.find((w) => w.id === widget.id);
          if (updatedWidget) {
            return {
              ...widget,
              ...updatedWidget,
              prepared: widget.prepared,
            };
          }
          return widget;
        }),
      };

      cardMapRef.current.set(card.cardId, updatedCard);

      updatedCard.widgets.forEach((widget) => {
        const widgetId = widget.id;

        let cardSet = widget2CardRef.current.get(widgetId);
        if (!cardSet || !(cardSet instanceof Set)) {
          cardSet = Array.isArray(cardSet) ? new Set(cardSet) : new Set();
        }
        cardSet.add(card.cardId);

        if (!widgetMapRef.current.has(widgetId)) {
          widgetMapRef.current.set(widgetId, widget);
        } else {
          const existingWidget = widgetMapRef.current.get(widgetId);
          widgetMapRef.current.set(widgetId, {
            ...existingWidget,
            ...widget,
          });
        }
      });

      setCardList(Array.from(cardMapRef.current.values()));
      setWidgetNames(
        Array.from(widgetMapRef.current.values()).map((w) => w.name)
      );

      if (card.layout && !isEqual(existingCard.layout, card.layout)) {
        const newLayouts = generateLayouts(
          [...cardList],
          getGridCols(isChatSidebarVisible),
          getCurrentLayoutSize()
        );
        setLayouts(newLayouts);
      }
    };

    const handleAutomationRunningEvent = (payload) => {
      const { automationId, running } = payload;

      for (const [widgetId, widget] of widgetMapRef.current.entries()) {
        if (widget.automationId === automationId) {
          const updatedWidget = {
            ...widget,
            running,
          };
          widgetMapRef.current.set(widgetId, updatedWidget);

          const cardIds = widget2CardRef.current.get(widgetId);
          if (cardIds) {
            cardIds.forEach((cardId) => {
              const card = cardMapRef.current.get(cardId);

              if (card) {
                const newWidgets = card.widgets.map((w) => {
                  if (w.id === widgetId) {
                    return {
                      ...w,
                      running,
                    };
                  }
                  return w;
                });

                const newCard = { ...card, widgets: newWidgets };
                cardMapRef.current.set(cardId, newCard);
              }
            });
          }
          setCardList(Array.from(cardMapRef.current.values()));
        }
      }
    };

    const getCurrentLayoutSize = useCallback(() => {
      const width = window.innerWidth;
      const effectiveWidth = isChatSidebarVisible ? width - 400 : width;
      if (effectiveWidth <= 768) return "sm";
      if (effectiveWidth <= 1200) return "md";
      return "lg";
    }, [isChatSidebarVisible]);

    const prepareCards = (cardList, widgetList, automations) => {
      const layoutSize = getCurrentLayoutSize();

      widgetMapRef.current.clear();
      cardMapRef.current.clear();

      const processedWidgets = widgetList.map((widget) => {
        if (widget.source === "automation") {
          const matchedAutomation = automations.find(
            (auto) => auto.automationId === widget.automationId
          );
          if (matchedAutomation) {
            return {
              ...widget,
              enabled: matchedAutomation.enabled,
              running: matchedAutomation.running,
              valid: matchedAutomation.valid,
              title: matchedAutomation.name,
              automationType: matchedAutomation.type,
            };
          }
        }
        return widget;
      });

      for (let i = 0; i < processedWidgets.length; i++) {
        widgetMapRef.current.set(processedWidgets[i].id, processedWidgets[i]);
      }
      setWidgetNames(
        Array.from(widgetMapRef.current.values()).map((w) => w.name)
      );

      const cards = cardList
        .map((cd) => {
          // Store all layout sizes
          const layouts = {
            sm: cd.layouts?.sm || { w: 2, h: 2, x: 0, y: 0 },
            md: cd.layouts?.md || { w: 2, h: 2, x: 0, y: 0 },
            lg: cd.layouts?.lg || { w: 2, h: 2, x: 0, y: 0 },
          };

          const detailedWidgets = cd.widgets
            .filter((widget) => widgetMapRef.current.has(widget.id))
            .map((widget) => {
              const fw = widgetMapRef.current.get(widget.id);
              const updated = { ...widget, ...fw };
              return updated;
            })
            .filter((widget) => widget !== undefined && widget !== null);

          detailedWidgets.forEach((widget) => {
            if (!widget2CardRef.current.has(widget.id)) {
              widget2CardRef.current.set(widget.id, [cd.cardId]);
            } else {
              const currentArray = widget2CardRef.current.get(widget.id);
              widget2CardRef.current.set(widget.id, [
                ...currentArray,
                cd.cardId,
              ]);
            }
          });

          return detailedWidgets.length > 0
            ? {
                id: cd.cardId,
                layouts: layouts,
                setting: cd.setting,
                widgets: detailedWidgets,
                titleWidgetId: cd.titleWidgetId,
                titleBindWidgetId: cd.titleBindWidgetId,
                meta: cd.meta,
                cardEntityAutomation: cd.cardEntityAutomation,
                tenantId: cd.tenantId,
                scopeId: cd.scopeId,
                canvasId: cd.canvasId,
              }
            : null;
        })
        .filter((group) => group !== null);

      for (let i = 0; i < cards.length; i++) {
        cardMapRef.current.set(cards[i].id, cards[i]);
      }

      setCardList(cards);
      const newLayouts = generateLayouts(
        cards,
        getGridCols(isChatSidebarVisible),
        layoutSize
      );
      setLayouts(newLayouts);
      isInitialized.current = true;
    };

    const loadCanvasData = async (force = false) => {
      if (
        (!canvasId || !wsReady || loadedCanvasId.current === canvasId) &&
        !force
      ) {
        return;
      }

      try {
        setContentLoading(true);

        setLayouts(null);
        setCardList([]);
        setWidgetNames([]);
        isInitialized.current = false;
        skipEvent.current = true;

        widget2CardRef.current.clear();
        cardMapRef.current.clear();
        widgetMapRef.current.clear();

        const response = await getCanvasContent(sendAndWaitWs, canvasId);

        if (response.error) {
          if (response.error !== "NOT_FOUND") {
            errorToast("Failed to load canvas!");
          }
          throw new Error(response.error);
        }

        prepareCards(
          response.canvas.cards,
          response.widgets,
          response.automations
        );

        closeBackdrop();
        skipEvent.current = false;

        setContentLoading(false);
        loadedCanvasId.current = canvasId;
      } catch (error) {
        console.error("Error fetching canvas data:", error);
        isInitialized.current = false;
        setContentLoading(false);
      }
    };

    // Load canvas data when canvasId or wsReady changes
    useEffect(() => {
      loadCanvasData();
    }, [canvasId, wsReady]);

    // Load canvas list for move operations
    useEffect(() => {
      const loadCanvasList = async () => {
        if (isCanvasListLoaded || canvasListLoadingRef.current) {
          return;
        }
        try {
          canvasListLoadingRef.current = true;
          const resp = await listCanvas(sendAndWaitWs);
          if (resp.canvas) {
            setCanvasList(resp.canvas);
            setIsCanvasListLoaded(true);
          } else {
            errorToast("Get Canvas Error");
          }
        } catch (error) {
          errorToast("Error to load canvas");
        } finally {
          canvasListLoadingRef.current = false;
        }
      };

      if (wsReady) {
        loadCanvasList();
      }
    }, [isCanvasListLoaded, wsReady]);

    const handleNewWidget = () => {
      setCurrentWidgets([]);
      setOpenWidgetDialog(true);
      setOpenOptionDialog(false);
    };

    const handleNewDeviceTwin = () => {
      setCurrentWidgets([]);
      setOpenDeviceDialog(true);
      setOpenOptionDialog(false);
    };

    const handleCloseWidgetDialog = () => {
      setOpenWidgetDialog(false);
    };

    const handleCloseDeviceDialog = () => {
      setOpenDeviceDialog(false);
    };

    const handleCloseOptionDialog = () => {
      setOpenOptionDialog(false);
    };

    const handleCloseCardDialog = () => {
      setOpenCardDialog(false);
    };

    const handleCloseDeleteDialog = () => {
      setOpenDeleteDialog(false);
    };

    const handleUiWidgetValueChange = async (widgetItem) => {
      console.debug("handleUiWidgetValueChange", widgetItem);
      const widgetItems = Array.isArray(widgetItem) ? widgetItem : [widgetItem];
      const resp = await updateWidgetData(sendAndWaitWs, widgetItems);
      if (!resp || resp.error) {
        errorToast("Error!");
      }
    };

    const handleTriggerWidgetEvent = useCallback(
      async (updateData) => {
        const resp = await triggerWidgetEvent(
          sendAndWaitWs,
          updateData.id,
          updateData.value
        );
        if (!resp || resp.error) {
          errorToast("Error!");
        }
      },
      [errorToast]
    );

    const handleCreateWidgetCard = async (title, widgetItems) => {
      if (widgetItems.length <= 0) {
        return;
      }

      setIsUpsertingWidget(true);
      skipEvent.current = true;
      try {
        const resp1 = await createWidgets(sendAndWaitWs, widgetItems);
        if (resp1 && !resp1.error) {
          const resp2 = await createWidgetCard(
            sendAndWaitWs,
            canvasId,
            title,
            resp1.items
          );
          if (resp2 && !resp2.error) {
            successToast("Saved");
            await loadCanvasData(true);
          } else {
            errorToast("Error! Check the name and config.");
          }
        } else {
          errorToast("Error! Check the name and config.");
        }
      } catch (error) {
        errorToast("Error! Check the name and config.");
      } finally {
        setIsUpsertingWidget(false);
        skipEvent.current = false;
      }
    };

    const handleUpsertDeviceTwin = () => {
      loadCanvasData(true);
    };

    const handleBeforeUpsertDeviceTwin = () => {
      skipEvent.current = true;
    };

    // TODO: no need to refresh the canvas
    const handleUpdateCard = async (card) => {
      const newCard = {
        id: card.id,
        name: card.name,
        widgetIds: card.widgets.map((w) => w.id),
      };
      skipEvent.current = true;
      const success = await updateCanvasCards(sendAndWaitWs, canvasId, [
        newCard,
      ]);
      if (success) {
        successToast("Saved");
        loadCanvasData(false);
        setOpenCardDialog(false);
      } else {
        errorToast("Error!");
      }
    };

    const handleDeleteCard = async (cardId) => {
      const success = await deleteCard(sendAndWaitWs, canvasId, cardId);
      if (success) {
        successToast("Deleted");
        setOpenCardDialog(false);
        await loadCanvasData(true);
      } else {
        errorToast("Remove card error!");
      }
    };

    const handleUpdateCardLayout = async () => {
      const cl = getCurrentLayoutSize();
      const layouts = cardList.map((c) => {
        return { id: c.id, layout: c.layout, layoutSize: cl };
      });

      // Check if any layout has actually changed
      const hasLayoutChanged = cardList.some((card) => {
        const currentLayout = card.layouts[cl];
        if (!currentLayout || !card.layout) {
          return false; // Skip if either layout is missing
        }
        return (
          currentLayout.x !== card.layout.x ||
          currentLayout.y !== card.layout.y ||
          currentLayout.w !== card.layout.w ||
          currentLayout.h !== card.layout.h
        );
      });

      if (!hasLayoutChanged) {
        return; // Skip API call if no changes
      }

      skipEvent.current = true;
      const success = await updateCardLayout(sendAndWaitWs, canvasId, layouts);
      if (success) {
        successToast("Saved");
        await loadCanvasData(true);
      } else {
        errorToast("Error!");
      }
    };

    const handleUpdateWidgetLayout = async (widgets) => {
      if (editCard) {
        setLoadingSave(true);
        try {
          const resp = await updateCardContent(
            sendAndWaitWs,
            editCard.id,
            widgets,
            null
          );
          if (resp) {
            successToast("Saved");
            handleWidgetLayoutChange(widgets);
          } else {
            errorToast("Error!");
          }
        } catch (error) {
          errorToast("Error!");
        } finally {
          setEditCard(null);
          setLoadingSave(false);
          setTempLayout(null);
          setOriginalCardLayout(null);
          setEditCardLayoutMode(false);
        }
      } else {
        errorToast("Error!");
        setEditCard(null);
        setTempLayout(null);
        setOriginalCardLayout(null);
        setEditCardLayoutMode(false);
      }
    };

    const handleEditWidgets = async (widgetIds) => {
      console.debug(widgetIds);
      const widgets = widgetIds.map((wid) => widgetMapRef.current.get(wid));
      setCurrentWidgets(widgets);
      setOpenWidgetDialog(true);
    };

    const handleOpenDeleteCard = async (card) => {
      setCurrentCard(card);
      setOpenDeleteDialog(true);
    };

    const handleOpenTwinAutomation = (card) => {
      history.push(`/automation/twin/list?cardId=${card.id}`);
    };

    const handleOpenAutomation = (card) => {
      console.debug(card);
      const aid = card.widgets
        .filter((widget) => widget.automationId)
        .map((widget) => widget.automationId)[0];
      history.push(`/automation/overview?id=${aid}`);
    };

    const getCardSource = (card) => {
      if (card.widgets.length > 0) {
        return card.widgets[0].source;
      }
      return "";
    };

    const handleCopyCard = async (card) => {
      const resp = await copyCard(sendAndWaitWs, canvasId, card.id);
      if (resp.success) {
        successToast("Copied");
        await loadCanvasData(true);
      } else {
        errorToast("Error!");
      }
    };

    const handleOpenMoveCard = (card) => {
      setCurrentMoveCard(card);
      setMoveDialogOpen(true);
    };

    const handleMoveCard = async (targetCanvasId) => {
      if (!targetCanvasId || !currentMoveCard) return;

      try {
        openBackdrop("Moving card...");
        const targetCanvas = canvasList.find((c) => c.id === targetCanvasId);

        const moveResponse = await moveCanvas(
          sendAndWaitWs,
          targetCanvasId,
          currentMoveCard.id,
          canvasId
        );

        if (moveResponse.success) {
          successToast(
            `Card moved to ${targetCanvas?.name || "target canvas"}`
          );
          await loadCanvasData(true);
          setMoveDialogOpen(false);
          setCurrentMoveCard(null);
        } else {
          throw new Error("Failed to move card");
        }
      } catch (error) {
        console.error("Error moving card:", error);
        errorToast("Error!");
      } finally {
        closeBackdrop();
      }
    };

    const handleUpdateEditCard = async (card) => {
      if (card?.setting) {
        const result = await updateCardContent(
          sendAndWaitWs,
          currentEditCard.id,
          null,
          card.setting
        );
        if (result) {
          successToast("Set Color Success");
        } else {
          errorToast("Color Error");
        }
      }
    };

    const handleCardLayoutChange = (currentLayout) => {
      // Ignore layout changes when dropping to stack
      if (droppingToStackRef.current) {
        return;
      }

      // Ignore layout changes when in edit card layout mode
      if (editCardLayoutMode) {
        return;
      }

      const updatedCardList = cardList.map((card) => {
        const layoutItem = currentLayout.find((layout) => layout.i === card.id);
        if (layoutItem) {
          card.layout = {
            ...card.layout,
            x: layoutItem.x,
            y: layoutItem.y,
            w: layoutItem.w,
            h: layoutItem.h,
          };
          return card;
        }
        return card;
      });

      setCardList(updatedCardList);
      setLayouts(currentLayout);
      setLayoutChanged(true);
    };

    const handleTempLayoutChange = (newLayout) => {
      setTempLayout(newLayout);
    };

    const handleWidgetLayoutChange = (currentLayout) => {
      if (editCard) {
        const updatedCardList = cardList.map((card) => {
          if (card.id === editCard.id) {
            const updatedWidgets = card.widgets.map((widget) => {
              const layoutItem = currentLayout.find(
                (layout) => layout.id === widget.id
              );
              console.debug("layoutItem", layoutItem);
              if (layoutItem) {
                return {
                  ...widget,
                  layouts: {
                    ...widget.layouts,
                    [layoutItem.layoutSize]: {
                      ...widget.layouts[layoutItem.layoutSize],
                      x: Number(layoutItem.layout.x),
                      y: Number(layoutItem.layout.y),
                      w: Number(layoutItem.layout.w),
                      h: Number(layoutItem.layout.h),
                    },
                  },
                };
              }
              return widget;
            });

            return {
              ...card,
              widgets: updatedWidgets,
            };
          }
          return card;
        });

        setCardList(updatedCardList);
        setLayoutChanged(true);
      }
    };

    const handleSaveEditCardLayout = async () => {
      if (tempLayout) {
        await handleUpdateWidgetLayout(tempLayout);
      } else {
        // Exit edit mode without saving changes
        setEditCard(null);
        setEditCardLayoutMode(false);
        setTempLayout(null);
        setOriginalCardLayout(null);
      }
    };

    const handleCancelEditCardLayout = () => {
      // Restore original card layout
      if (originalCardLayout && editCard) {
        const updatedCardList = cardList.map((card) => {
          if (card.id === editCard.id) {
            // Restore original card with original layout - create a new object to force re-render
            return JSON.parse(JSON.stringify(originalCardLayout));
          }
          return card;
        });
        setCardList(updatedCardList);
        setLayoutChanged(true);

        // Force update layouts to reflect restored card
        const layoutSize = getCurrentLayoutSize();
        const newLayouts = generateLayouts(
          updatedCardList,
          getGridCols(isChatSidebarVisible),
          layoutSize
        );
        setLayouts(newLayouts);
      }
      setEditCard(null);
      setEditCardLayoutMode(false);
      setTempLayout(null);
      setOriginalCardLayout(null);
    };

    const handleMultiSelect = (cardId) => {
      setIsMultiSelectMode(true);
      setAnchorElMap({}); // Close all menus
      if (cardId) {
        setSelectedCards([cardId]);
      } else {
        setSelectedCards([]);
      }
    };

    const handleBatchDelete = async () => {
      setIsBatchDeleteDialogOpen(true);
    };

    const handleBatchDeleteConfirm = async () => {
      setIsBatchDeleting(true);
      try {
        await batchDeleteCard(sendAndWaitWs, canvasId, selectedCards);
        setSelectedCards([]);
        successToast("Cards deleted successfully");
        await loadCanvasData(true);
      } catch (error) {
        errorToast("Failed to delete cards");
      } finally {
        setIsBatchDeleting(false);
        setIsBatchDeleteDialogOpen(false);
      }
    };

    const handleBatchMove = async () => {
      setIsBatchMoveDialogOpen(true);
    };

    const handleBatchMoveConfirm = async (targetCanvasId) => {
      if (!targetCanvasId || selectedCards.length === 0) return;

      setIsBatchMoving(true);
      try {
        openBackdrop(`Moving ${selectedCards.length} cards...`);
        const targetCanvas = canvasList.find((c) => c.id === targetCanvasId);

        // Move all selected cards using batch API
        const result = await batchMoveCanvas(
          sendAndWaitWs,
          targetCanvasId,
          selectedCards,
          canvasId
        );

        if (result.success) {
          successToast(
            `${selectedCards.length} cards moved to ${
              targetCanvas?.name || "target canvas"
            }`
          );
          setSelectedCards([]);
          setIsMultiSelectMode(false);
          await loadCanvasData(true);
          setIsBatchMoveDialogOpen(false);
        } else {
          throw new Error(result.error || "Failed to move cards");
        }
      } catch (error) {
        console.error("Error moving cards:", error);
        errorToast("Failed to move cards");
      } finally {
        setIsBatchMoving(false);
        closeBackdrop();
      }
    };

    const handleCardSelect = (cardId) => {
      setSelectedCards((prev) => {
        if (prev.includes(cardId)) {
          return prev.filter((id) => id !== cardId);
        } else {
          return [...prev, cardId];
        }
      });
    };

    const handleSelectAll = () => {
      if (selectedCards.length === cardList.length) {
        // If all cards are selected, clear selection
        setSelectedCards([]);
      } else {
        // Select all cards
        setSelectedCards(cardList.map((card) => card.id));
      }
    };

    // Calculate responsive breakpoints based on chat sidebar visibility
    const getResponsiveBreakpoints = () => {
      const baseBreakpoints = {
        lg: 1200,
        md: 996,
        sm: 768,
        xs: 480,
        xxs: 0,
      };

      if (isChatSidebarVisible) {
        return {
          lg: baseBreakpoints.lg + 400,
          md: baseBreakpoints.md + 400,
          sm: baseBreakpoints.sm + 400,
          xs: baseBreakpoints.xs + 400,
          xxs: baseBreakpoints.xxs + 400,
        };
      }
      return baseBreakpoints;
    };

    const getResponsiveCols = () => {
      const col = getGridCols(isChatSidebarVisible);
      const baseCols = {
        lg: col,
        md: col,
        sm: col,
        xs: col,
        xxs: col,
      };

      return baseCols;
    };

    const scrollLoop = useCallback(() => {
      if (!containerRef.current || scrollDirectionRef.current === 0) {
        if (scrollAnimationRef.current) {
          cancelAnimationFrame(scrollAnimationRef.current);
          scrollAnimationRef.current = null;
        }
        return;
      }

      const container = containerRef.current;
      const scrollSpeed = 15;

      if (scrollDirectionRef.current === -1) {
        // scroll up
        container.scrollTop = Math.max(0, container.scrollTop - scrollSpeed);
      } else if (scrollDirectionRef.current === 1) {
        // scroll down
        const maxScroll = container.scrollHeight - container.clientHeight;
        container.scrollTop = Math.min(
          maxScroll,
          container.scrollTop + scrollSpeed
        );
      }

      scrollAnimationRef.current = requestAnimationFrame(scrollLoop);
    }, []);

    const handleDragStop = (
      layout,
      oldItem,
      newItem,
      placeholder,
      e,
      element
    ) => {
      if (!drag) return;

      // Reset drag over stack state
      setIsDraggingOverStack(false);

      // Clear auto-scroll
      scrollDirectionRef.current = 0;
      if (scrollAnimationRef.current) {
        cancelAnimationFrame(scrollAnimationRef.current);
        scrollAnimationRef.current = null;
      }

      // Check if item was dropped in the stack area
      const mouseY =
        e.type === "touchend"
          ? e.changedTouches?.[0]?.clientY || 0
          : e.clientY || 0;
      const mouseX =
        e.type === "touchend"
          ? e.changedTouches?.[0]?.clientX || 0
          : e.clientX || 0;

      // Get the stack container element to check if drop is in stack area
      const stackContainer = document.querySelector(".temp-stack-container");
      let droppedInStack = false;

      if (stackContainer) {
        const stackRect = stackContainer.getBoundingClientRect();

        // Check if mouse is within the stack container bounds (left sidebar)
        if (
          mouseX >= stackRect.left &&
          mouseX <= stackRect.right &&
          mouseY >= stackRect.top &&
          mouseY <= stackRect.bottom
        ) {
          droppedInStack = true;
          // Set flag to prevent layout change handling
          droppingToStackRef.current = true;

          // Item was dropped in the stack area
          const itemData = {
            i: newItem.i,
            w: newItem.w,
            h: newItem.h,
            x: oldItem.x, // Use original position
            y: oldItem.y,
          };
          handleDropToStack(itemData);

          // Reset flag after a short delay
          setTimeout(() => {
            droppingToStackRef.current = false;
          }, 100);
        }
      }

      // Clean up element styles
      element.style.opacity = "";
      element.style.boxShadow = "";
      element.style.zIndex = "";
      element.style.transform = "";

      // If dropped in stack, don't continue with normal layout update
      if (droppedInStack) {
        return;
      }
    };

    const handleDrag = (layout, oldItem, newItem, placeholder, e, element) => {
      if (!drag || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const mouseY = e.type === "touchmove" ? e.touches[0].clientY : e.clientY;
      const mouseX = e.type === "touchmove" ? e.touches[0].clientX : e.clientX;
      const scrollThreshold = 80;

      const relativeY = mouseY - containerRect.top;
      const containerHeight = containerRect.height;

      // Check if dragging over stack area and provide visual feedback
      const stackContainer = document.querySelector(".temp-stack-container");
      let isOverStack = false;
      if (stackContainer) {
        const stackRect = stackContainer.getBoundingClientRect();
        isOverStack =
          mouseX >= stackRect.left &&
          mouseX <= stackRect.right &&
          mouseY >= stackRect.top &&
          mouseY <= stackRect.bottom;
      }

      // Update drag over stack state
      setIsDraggingOverStack(isOverStack);

      if (isOverStack) {
        element.style.opacity = "0.7";
        element.style.transform = "scale(0.95)";
        element.style.boxShadow = "0 8px 25px rgba(0,0,0,0.3)";
      } else {
        element.style.opacity = "";
        element.style.transform = "";
        element.style.boxShadow = "";
      }

      let newScrollDirection = 0;
      if (relativeY < scrollThreshold && !isOverStack) {
        newScrollDirection = -1; // up
      } else if (containerHeight - relativeY < scrollThreshold) {
        newScrollDirection = 1; // down
      }

      if (newScrollDirection !== scrollDirectionRef.current) {
        scrollDirectionRef.current = newScrollDirection;
        if (newScrollDirection !== 0 && !scrollAnimationRef.current) {
          scrollLoop();
        }
      }
    };

    // Handle dropping items to the temporary stack
    const handleDropToStack = (itemData) => {
      // Find the card data from cardList
      if (!cardList || !Array.isArray(cardList)) {
        console.warn("cardList is not available for stack operation");
        return;
      }

      const cardData = cardList.find((card) => card.id === itemData.i);
      if (!cardData) {
        console.warn(`Card data not found for item ${itemData.i}`, {
          itemId: itemData.i,
          availableCards: cardList.map((c) => c.id),
        });
        return;
      }

      // Store the item with its original size and card data
      const stackItem = {
        i: itemData.i,
        w: itemData.w,
        h: itemData.h,
        x: itemData.x,
        y: itemData.y,
        title: getCardName(cardData),
        cardData: cardData,
      };

      // Add to stack if not already there
      setStackedItems((prev) => {
        const exists = prev.find((item) => item.i === itemData.i);
        if (exists) return prev;
        return [...prev, stackItem];
      });
      stackedIds.current.add(itemData.i);

      setLayouts((prevLayouts) => {
        if (!prevLayouts || !Array.isArray(prevLayouts)) return prevLayouts;

        // layouts is an array, filter it directly
        return prevLayouts.filter((item) => item.i !== itemData.i);
      });
    };

    // Handle dragging items from the temporary stack back to grid
    const handleDragFromStack = (
      item,
      position = { x: 0, y: 0 },
      cursorPosition = { x: 0, y: 0 }
    ) => {
      if (!position) {
        position = calculateGridPosition(
          cursorPosition.x,
          cursorPosition.y,
          containerRef.current,
          layouts,
          isChatSidebarVisible
        );
      }

      console.debug("Restoring item from temp stack:", {
        id: item.i,
        originalDimensions: { w: item.w, h: item.h },
        newPosition: position,
        fullItem: item,
      });

      // Remove from stack
      setStackedItems((prev) =>
        prev.filter((stackItem) => stackItem.i !== item.i)
      );
      stackedIds.current.delete(item.i);

      // Add back to layout with original dimensions and specified position
      setLayouts((prevLayouts) => {
        if (!prevLayouts || !Array.isArray(prevLayouts)) return prevLayouts;

        const filteredLayouts = prevLayouts.filter((x) => x.i !== item.i);

        const layoutItem = {
          i: item.i,
          x: position.x,
          y: position.y,
          w: item.w,
          h: item.h,
        };
        var newLayouts = [...filteredLayouts, layoutItem];
        return newLayouts;
      });
    };

    // Handle drop events for chips from stack
    const handleGridDrop = (e) => {
      e.preventDefault();

      // Clear preview and placeholder
      setChipDragPreview(null);
      setLayouts((prevLayouts) => {
        if (!prevLayouts || !Array.isArray(prevLayouts)) return prevLayouts;
        return prevLayouts.filter((item) => item.i !== "__chip_placeholder__");
      });

      try {
        const dragData = e.dataTransfer.getData("text/plain");
        const parsedData = JSON.parse(dragData);

        if (parsedData.type === "stackItem" && parsedData.item) {
          const dropPosition = calculateGridPosition(
            e.clientX,
            e.clientY,
            containerRef.current,
            layouts,
            isChatSidebarVisible
          );

          handleDragFromStack(parsedData.item, dropPosition);
        }
      } catch (error) {
        console.error("Error handling grid drop:", error);
      }
    };

    const handleGridDragOver = (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";

      // Check if we're dragging a chip from stack
      try {
        const draggedItem = window.draggedStackItem;
        if (draggedItem) {
          const dropPosition = calculateGridPosition(
            e.clientX,
            e.clientY,
            containerRef.current,
            layouts,
            isChatSidebarVisible
          );

          // Update layouts to include placeholder at drop position
          setLayouts((prevLayouts) => {
            if (!prevLayouts || !Array.isArray(prevLayouts)) return prevLayouts;

            // Remove any existing placeholder
            const layoutsWithoutPlaceholder = prevLayouts.filter(
              (item) => item.i !== "__chip_placeholder__"
            );

            // Add new placeholder at current position
            const placeholder = {
              i: "__chip_placeholder__",
              x: dropPosition.x,
              y: dropPosition.y,
              w: draggedItem.w,
              h: draggedItem.h,
              isDragging: true,
              isPlaceholder: true,
            };

            return [...layoutsWithoutPlaceholder, placeholder];
          });

          setChipDragPreview({
            item: draggedItem,
            position: dropPosition,
          });
        }
      } catch (error) {
        // Silently ignore errors during drag over
      }
    };

    const handleGridDragLeave = (e) => {
      // Only clear preview if we're leaving the main container
      if (!containerRef.current?.contains(e.relatedTarget)) {
        setChipDragPreview(null);

        // Remove placeholder from layouts
        setLayouts((prevLayouts) => {
          if (!prevLayouts || !Array.isArray(prevLayouts)) return prevLayouts;
          return prevLayouts.filter(
            (item) => item.i !== "__chip_placeholder__"
          );
        });
      }
    };

    const handleChipDragEnd = () => {
      // Clear chip drag preview
      setChipDragPreview(null);

      // Remove placeholder from layouts
      setLayouts((prevLayouts) => {
        if (!prevLayouts || !Array.isArray(prevLayouts)) return prevLayouts;
        return prevLayouts.filter((item) => item.i !== "__chip_placeholder__");
      });
    };

    // Get card name from title widgets
    const getCardName = (card) => {
      if (!card || !card.widgets) return "Untitled Card";

      const titleWidget = card.widgets.find((w) => w.type === "title");
      if (titleWidget && titleWidget.title) {
        return titleWidget.title;
      }

      // Fallback to card title or ID
      return card.title || card.name || `Card ${card.id}`;
    };

    // Get combined card list including placeholder
    const getCombinedCardList = () => {
      const filteredCardList = cardList.filter(
        (card) => !stackedIds.current.has(card.id)
      );

      const hasPlaceholder =
        layouts && layouts.some((item) => item.i === "__chip_placeholder__");
      if (!hasPlaceholder || !chipDragPreview) {
        return filteredCardList;
      }

      // Create a placeholder card with essential attributes
      const placeholderCard = {
        id: "__chip_placeholder__",
        title: chipDragPreview.item.title,
        isPlaceholder: true,
        widgets: [],
        layouts: {
          sm: { w: chipDragPreview.item.w, h: chipDragPreview.item.h },
          md: { w: chipDragPreview.item.w, h: chipDragPreview.item.h },
          lg: { w: chipDragPreview.item.w, h: chipDragPreview.item.h },
        },
        // Include additional attributes if they exist in the original card
        titleWidgetId: chipDragPreview.item.cardData?.titleWidgetId,
        titleBindWidgetId: chipDragPreview.item.cardData?.titleBindWidgetId,
        meta: chipDragPreview.item.cardData?.meta,
        cardEntityAutomation:
          chipDragPreview.item.cardData?.cardEntityAutomation,
        setting: chipDragPreview.item.cardData?.setting,
      };

      return [...filteredCardList, placeholderCard];
    };

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
      openOptionDialog: () => {
        setOpenOptionDialog(true);
      },
    }));

    // Get the background color function with current global state
    const getComponentBgColor = useComponentBackgroundColor();

    return (
      <div className="flex-1 flex relative">
        {/* Left sidebar with temporary stack - only show in drag mode */}
        {drag && (
          <div
            className="w-20 flex-shrink-0 border-r border-solid relative"
            style={{
              borderColor: "hsl(var(--border))",
              backgroundColor: getComponentBgColor("hsl(var(--card))"),
            }}
          >
            <TempStack
              stackedItems={stackedItems}
              onDragFromStack={handleDragFromStack}
              isDragOver={isDraggingOverStack}
              onChipDragEnd={handleChipDragEnd}
              getCardName={getCardName}
              onMobileDragOver={handleGridDragOver}
            />
          </div>
        )}

        {/* Main canvas area */}
        <div
          ref={containerRef}
          onDrop={handleGridDrop}
          onDragOver={handleGridDragOver}
          onDragLeave={handleGridDragLeave}
          className={`flex-1 overflow-auto relative pb-4 pr-2 ${
            drag ? "pl-7" : "pl-2"
          }`}
        >
          {contentLoading ? (
            <div className="flex justify-center items-center h-full"></div>
          ) : (
            <>
              <style>
                {`
            `}
              </style>
              <>
                <ResponsiveGridLayout
                  className="layout"
                  layouts={{
                    lg: Array.isArray(layouts) ? layouts : [],
                    md: Array.isArray(layouts) ? layouts : [],
                    sm: Array.isArray(layouts) ? layouts : [],
                    xs: Array.isArray(layouts) ? layouts : [],
                    xxs: Array.isArray(layouts) ? layouts : [],
                  }}
                  breakpoints={getResponsiveBreakpoints()}
                  cols={getResponsiveCols()}
                  rowHeight={16}
                  onLayoutChange={handleCardLayoutChange}
                  compactType={null}
                  preventCollision={true}
                  isDraggable={drag}
                  isResizable={drag}
                  margin={[0, 0]}
                  useCSSTransforms={false}
                  draggableCancel=".non-draggable"
                  transitionDuration={0}
                  animate={true}
                  autoSize={false}
                  onDragStop={handleDragStop}
                  onDrag={handleDrag}
                  style={{
                    userSelect: "none",
                    WebkitUserSelect: "none",
                    MozUserSelect: "none",
                    msUserSelect: "none",
                    WebkitTapHighlightColor: "transparent",
                    touchAction: "manipulation",
                  }}
                >
                  {getCombinedCardList().map((card) => (
                    <div
                      key={card.id}
                      className={`absolute flex flex-col box-border p-2 ${
                        drag ? "cursor-move" : "cursor-pointer"
                      } ${
                        editCardLayoutMode && card.id === editCard?.id
                          ? "z-[1000]"
                          : "z-auto"
                      } ${
                        editCardLayoutMode
                          ? "overflow-visible"
                          : "overflow-hidden"
                      }`}
                      style={{ opacity: 1 }}
                    >
                      {isMultiSelectMode && !editCardLayoutMode && (
                        <div
                          className="absolute top-2 right-2 z-[1001] w-10 h-10 rounded-2xl flex items-center justify-center"
                          style={{
                            backgroundColor: "rgba(255, 255, 255, 0.1)",
                            backdropFilter: "blur(10px)",
                            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.12)",
                          }}
                        >
                          <Checkbox
                            onMouseDown={(e) => e.stopPropagation()}
                            checked={selectedCards.includes(card.id)}
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              handleCardSelect(card.id);
                            }}
                          />
                        </div>
                      )}
                      <Menu
                        anchorEl={getCardAnchorEl(card.id)}
                        open={Boolean(getCardAnchorEl(card.id))}
                        onClose={() => {
                          closeCardMenu(card.id);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        anchorOrigin={{
                          vertical: "bottom",
                          horizontal: "left",
                        }}
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "left",
                        }}
                      >
                        <MenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleMultiSelect(card.id);
                            closeCardMenu(card.id);
                          }}
                        >
                          <MenuItemIcon>
                            <CheckBoxOutlineBlankIcon />
                          </MenuItemIcon>
                          <MenuItemText>Multi Select</MenuItemText>
                        </MenuItem>
                        <MenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            // Save original card layout before entering edit mode
                            const originalCard = cardList.find(
                              (c) => c.id === card.id
                            );
                            if (originalCard) {
                              // Deep clone the card to preserve original layout
                              setOriginalCardLayout(
                                JSON.parse(JSON.stringify(originalCard))
                              );
                            }
                            setEditCard(card);
                            setEditCardLayoutMode(true);
                            closeCardMenu(card.id);
                          }}
                        >
                          <MenuItemIcon>
                            <GridOnIcon />
                          </MenuItemIcon>
                          <MenuItemText>Layout</MenuItemText>
                        </MenuItem>
                        {getCardSource(card) === "twin" &&
                          userInfo.setting?.debugMode && (
                            <MenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                handleOpenTwinAutomation(card);
                                closeCardMenu(card.id);
                              }}
                            >
                              <MenuItemIcon>
                                <AutoModeIcon />
                              </MenuItemIcon>
                              <MenuItemText>Automation</MenuItemText>
                            </MenuItem>
                          )}
                        {getCardSource(card) === "automation" && (
                          <MenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              handleOpenAutomation(card);
                              closeCardMenu(card.id);
                            }}
                          >
                            <MenuItemIcon>
                              <AutoModeIcon />
                            </MenuItemIcon>
                            <MenuItemText>Automation</MenuItemText>
                          </MenuItem>
                        )}
                        <MenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleCopyCard(card);
                            closeCardMenu(card.id);
                          }}
                        >
                          <MenuItemIcon>
                            <ContentCopy />
                          </MenuItemIcon>
                          <MenuItemText>Copy</MenuItemText>
                        </MenuItem>
                        <MenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleOpenMoveCard(card);
                            closeCardMenu(card.id);
                          }}
                        >
                          <MenuItemIcon>
                            <SwapHorizIcon />
                          </MenuItemIcon>
                          <MenuItemText>Move</MenuItemText>
                        </MenuItem>
                        <MenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleOpenDeleteCard(card);
                            closeCardMenu(card.id);
                          }}
                        >
                          <MenuItemIcon>
                            <DeleteIcon
                              style={{ color: "hsl(var(--destructive))" }}
                            />
                          </MenuItemIcon>
                          <MenuItemText
                            style={{ color: "hsl(var(--destructive))" }}
                          >
                            Delete
                          </MenuItemText>
                        </MenuItem>
                      </Menu>
                      {(drag || isMultiSelectMode) && !editCardLayoutMode && (
                        <div
                          className="absolute top-0 left-0 right-0 z-10 bg-transparent cursor-move pointer-events-auto"
                          style={{ bottom: 20 }}
                        />
                      )}
                      {(drag || isMultiSelectMode) && !editCardLayoutMode && (
                        <div
                          className="absolute left-0 bottom-0 z-10 bg-transparent cursor-move pointer-events-auto h-5"
                          style={{ width: "calc(100% - 20px)" }}
                        />
                      )}
                      <Paper
                        elevation={0}
                        data-card-id={card.id}
                        onClick={(e) => {
                          if (isEditTitle === card.id) {
                            e.stopPropagation();
                            return;
                          }
                        }}
                        className={`h-full w-full p-2 flex flex-col justify-between rounded-2xl box-border max-h-full max-w-full relative ${
                          editCardLayoutMode
                            ? "overflow-visible"
                            : "overflow-hidden"
                        } ${
                          editCardLayoutMode && card.id === editCard?.id
                            ? "z-[1]"
                            : "z-auto"
                        }`}
                        style={{
                          backgroundColor:
                            getCardColor(
                              card?.setting?.backgroundColor,
                              document.documentElement.classList.contains(
                                "dark"
                              )
                                ? "dark"
                                : "light"
                            ) === "default"
                              ? getComponentBgColor("hsl(var(--card))")
                              : getCardColor(
                                  card?.setting?.backgroundColor,
                                  document.documentElement.classList.contains(
                                    "dark"
                                  )
                                    ? "dark"
                                    : "light"
                                ),
                          opacity: editCardLayoutMode
                            ? card.id === editCard?.id
                              ? 1
                              : 0
                            : drag || isMultiSelectMode
                            ? 0.8
                            : 1,
                          filter: "none",
                          boxShadow: "none",
                          transform: "scale(1)",
                        }}
                      >
                        {card.isPlaceholder ? (
                          <div
                            className="w-full h-full rounded-2xl flex items-center justify-center pointer-events-none react-grid-placeholder"
                            style={{
                              backgroundColor: "#ff6666",
                              opacity: 0.6,
                              border: "2px dashed #cc0000",
                            }}
                          >
                            <Typography
                              variant="body2"
                              className="text-white font-semibold text-center"
                            >
                              {card.title}
                            </Typography>
                          </div>
                        ) : (
                          <DashboardCard
                            key={`${card.id}-${
                              editCardLayoutMode ? "edit" : "normal"
                            }-${card.widgets
                              ?.map(
                                (w) =>
                                  `${w.id}-${
                                    w.layouts?.[getCurrentLayoutSize()]?.x
                                  }-${w.layouts?.[getCurrentLayoutSize()]?.y}-${
                                    w.layouts?.[getCurrentLayoutSize()]?.w
                                  }-${w.layouts?.[getCurrentLayoutSize()]?.h}`
                              )
                              .join("-")}`}
                            card={card}
                            drag={
                              editCardLayoutMode && card.id === editCard?.id
                            }
                            onStateUpdate={handleWidgetUpdateEvent}
                            onLayoutChange={
                              editCardLayoutMode && card.id === editCard?.id
                                ? handleTempLayoutChange
                                : undefined
                            }
                            onValueUpdate={handleUiWidgetValueChange}
                            onTriggerEvent={handleTriggerWidgetEvent}
                            layoutSize={getCurrentLayoutSize()}
                            onTitleHold={(anchorEl) => {
                              // Show the context menu
                              if (!editCardLayoutMode) {
                                // Ensure anchorEl is a valid DOM element
                                if (anchorEl && anchorEl.nodeType === 1) {
                                  // Use requestAnimationFrame to ensure DOM is ready
                                  requestAnimationFrame(() => {
                                    setCardAnchorEl(card.id, anchorEl);
                                  });
                                } else {
                                  // If anchorEl is not valid, try to find the card element
                                  const cardElement = document.querySelector(
                                    `[data-card-id="${card.id}"]`
                                  );
                                  if (cardElement) {
                                    requestAnimationFrame(() => {
                                      setCardAnchorEl(card.id, cardElement);
                                    });
                                  }
                                }
                              }
                            }}
                          ></DashboardCard>
                        )}
                      </Paper>
                    </div>
                  ))}
                </ResponsiveGridLayout>
              </>
            </>
          )}
        </div>

        {/* Edit Card Layout mode bottom bar */}
        {editCardLayoutMode && (
          <div
            className="fixed bottom-0 left-0 px-4 py-3 flex justify-between items-center"
            style={{
              width:
                isMdOrLgScreen && isChatSidebarVisible
                  ? "calc(100vw - 400px)"
                  : "100vw",
              backgroundColor: "hsl(var(--card))",
              boxShadow: "0px -2px 4px rgba(0,0,0,0.1)",
              zIndex: "var(--z-index-drawer)",
            }}
          >
            <Typography variant="h6">Edit Card Layout</Typography>
            <div className="flex items-center gap-2">
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCancelEditCardLayout();
                }}
                variant="outlined"
                className="normal-case"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveEditCardLayout}
                variant="contained"
                color="primary"
                disabled={loadingSave}
                className="flex items-center gap-2 normal-case"
              >
                Save
                {loadingSave && (
                  <CircularProgress
                    size={20}
                    style={{
                      color: "inherit",
                    }}
                  />
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Multi-select mode bottom bar */}
        {isMultiSelectMode && (
          <div
            className="fixed bottom-0 left-0 px-4 py-3 flex justify-between items-center"
            style={{
              width:
                isMdOrLgScreen && isChatSidebarVisible
                  ? "calc(100vw - 400px)"
                  : "100vw",
              backgroundColor: "hsl(var(--card))",
              boxShadow: "0px -2px 4px rgba(0,0,0,0.1)",
              zIndex: "var(--z-index-drawer)",
            }}
          >
            <Typography>{selectedCards.length} selected</Typography>
            <div className="flex items-center gap-2">
              <Checkbox
                checked={
                  selectedCards.length === cardList.length &&
                  cardList.length > 0
                }
                indeterminate={
                  selectedCards.length > 0 &&
                  selectedCards.length < cardList.length
                }
                onChange={handleSelectAll}
                className="[&_svg]:text-2xl"
                aria-label={
                  selectedCards.length === cardList.length
                    ? "Clear selection"
                    : "Select all"
                }
              />
              <IconButton
                onClick={handleBatchMove}
                disabled={selectedCards.length === 0 || isBatchMoving}
                style={{
                  color:
                    selectedCards.length === 0
                      ? "hsl(var(--muted-foreground))"
                      : "hsl(var(--primary))",
                }}
                aria-label="Move cards"
              >
                {isBatchMoving ? (
                  <CircularProgress size={24} />
                ) : (
                  <SwapHorizIcon />
                )}
              </IconButton>
              <IconButton
                onClick={handleBatchDelete}
                disabled={selectedCards.length === 0 || isBatchDeleting}
                style={{
                  color:
                    selectedCards.length === 0
                      ? "hsl(var(--muted-foreground))"
                      : "hsl(var(--destructive))",
                }}
                aria-label="Delete cards"
              >
                {isBatchDeleting ? (
                  <CircularProgress size={24} />
                ) : (
                  <DeleteIcon />
                )}
              </IconButton>
              <IconButton
                onClick={() => {
                  setSelectedCards([]);
                  setIsMultiSelectMode(false);
                }}
                style={{
                  color: "hsl(var(--muted-foreground))",
                }}
                aria-label="Cancel"
              >
                <CloseIcon />
              </IconButton>
            </div>
          </div>
        )}

        {/* Dialogs */}
        <AddOptionDialog
          open={openOptionDialog}
          onClose={handleCloseOptionDialog}
          onDeviceSelect={handleNewDeviceTwin}
          onWidgetSelect={handleNewWidget}
        />
        <UpsertWidgetDialog
          open={openWidgetDialog}
          currentWidgets={currentWidgets}
          existingNames={widgetNames}
          onClose={handleCloseWidgetDialog}
          onCreate={handleCreateWidgetCard}
          isUpserting={isUpsertingWidget}
        />
        <UpsertDeviceDialog
          open={openDeviceDialog}
          onClose={handleCloseDeviceDialog}
          onBeforeSave={handleBeforeUpsertDeviceTwin}
          onSave={handleUpsertDeviceTwin}
          currentCanvasId={canvasId}
        />
        <DeleteCardDialog
          open={openDeleteDialog}
          card={currentCard}
          onClose={handleCloseDeleteDialog}
          onDelete={handleDeleteCard}
        />
        <EditCardDialog
          open={openCardDialog}
          card={currentCard}
          onEditWidgets={handleEditWidgets}
          onClose={handleCloseCardDialog}
          onSave={handleUpdateCard}
          onDelete={handleDeleteCard}
        />
        <CardContentEditDialog
          open={isEditTitle === currentEditCard?.id}
          card={currentEditCard}
          onClose={() => {
            setIsEditTitle(false);
          }}
          onSave={handleUpdateEditCard}
        />
        <CanvasSelectDialog
          open={moveDialogOpen}
          onClose={() => {
            setMoveDialogOpen(false);
          }}
          onCanvasChange={handleMoveCard}
          canvasList={canvasList}
          unavailableCanvasList={[canvasId]}
          title="Select Target Canvas"
        />
        <BatchDeleteDialog
          open={isBatchDeleteDialogOpen}
          count={selectedCards.length}
          onClose={() => setIsBatchDeleteDialogOpen(false)}
          onDelete={handleBatchDeleteConfirm}
        />
        <CanvasSelectDialog
          open={isBatchMoveDialogOpen}
          onClose={() => {
            setIsBatchMoveDialogOpen(false);
          }}
          onCanvasChange={handleBatchMoveConfirm}
          canvasList={canvasList}
          unavailableCanvasList={[canvasId]}
          title={`Move ${selectedCards.length} Cards`}
          isLoading={isBatchMoving}
        />

        {/* Dashboard Guide Dialog */}
        <Dialog
          open={isGuideDialogOpen}
          onClose={() => setIsGuideDialogOpen(false)}
          variant="pops"
        >
          <DialogTitle className="flex justify-between items-center pb-2">
            <Typography variant="h6" component="div">
              Dashboard Layout Guide
            </Typography>
            <IconButton
              onClick={() => setIsGuideDialogOpen(false)}
              size="small"
              style={{
                color: "hsl(var(--muted-foreground))",
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent className="pt-4">
            <Markdown>{guideMarkdownContent}</Markdown>
          </DialogContent>
        </Dialog>
      </div>
    );
  }
);

DashboardCanvas.displayName = "DashboardCanvas";
export default DashboardCanvas;
